import { useState, type FormEvent, type ReactNode } from 'react';
import { SUPPORT_LIMITS, SUPPORT_TOPICS, parseRoomId, type RoomId, type SupportTopic } from '@engineer-os/chat';
import { useLocale, useStrings } from '../../state/useLocale';
import { Button, Parts, Tag } from '../../ui';
import { shortDate } from '../../lib/format';
import type { SupportThread } from '../../lib/support';
import type { SupportApi } from '../../state/useSupport';

/**
 * The door to a conversation with whoever runs this deployment.
 *
 * ## Why this is in the chat panel and not on a page of its own
 *
 * Because a support thread *is* a room, and the panel is where rooms are. A
 * learner whose card was charged twice needs to describe what happened,
 * paste a reference, probably attach a screenshot and then come back
 * tomorrow to read the answer — which is a conversation, and the panel
 * already does every part of it. A contact form would have been a second,
 * worse composer with no history, no pictures and no way to reply, built
 * beside one that has all three.
 *
 * So this popover is deliberately small. It does the three things the room
 * cannot do for itself — say whether anybody is at the desk, start a thread
 * and close one — and then gets out of the way by opening the room.
 *
 * ## Why opening it is `join` and not `show`
 *
 * `chat.join` pins a room as home. A learner who opens their support thread
 * and then walks into the next level must still be in it when they get
 * there: they are mid-sentence with a person, and having the conversation
 * swapped out from under them by the level's room is the one way this
 * feature would be experienced as broken. `show` would do exactly that.
 * Teams made the same call for the same reason (ChatPanel.tsx, `onOpen`).
 *
 * ## Why the room id comes off the wire
 *
 * `thread.room`, parsed, rather than `supportRoom(thread.id)` assembled
 * here. The client's own note says why (`lib/support.ts`,
 * `SupportThread.room`): if the family ever gains a prefix or a shard, a tab
 * that built the id itself would open a socket onto a room nobody is in, and
 * it would do it silently. Parsing is not building — it is refusing to hand
 * `chat.join` something that is not a room id, which is the one thing that
 * could come back from a Worker a deploy ahead of this bundle.
 *
 * ## What it says out loud
 *
 * **Whoever runs EngineerOS reads this conversation.** Last, under
 * everything it is about, and not behind a disclosure — `Teams.tsx` set the
 * precedent and this is the stronger case for it. A team room is read by an
 * operator who *could* look; a support thread is read by one who certainly
 * will, because reading it is how the question gets answered. The sentence
 * is drawn in every state this component has, including signed out and
 * switched off, because the moment it is worth reading is the moment before
 * somebody decides to type an order number into it.
 */
export interface SupportProps {
  support: SupportApi;
  /** False while signed out: a thread belongs to an account, so there is nothing to show. */
  signedIn: boolean;
  /** Open a thread's room. The id came off the wire; this panel never builds one. */
  onOpen(room: RoomId): void;
  /** Which room is on screen, so the thread whose room it is reads as current. */
  active: RoomId;
}

export function Support({ support, signedIn, onOpen, active }: SupportProps) {
  const s = useStrings().chat;
  const locale = useLocale().locale;
  // Null until chosen, and never defaulted: a topic nobody picked is a topic
  // nobody meant, and `access` sitting pre-selected would fill the queue's
  // commonest category with questions that are not about it.
  const [topic, setTopic] = useState<SupportTopic | null>(null);
  const [subject, setSubject] = useState('');
  // One refusal at a time. There is one form and one thread here, and a
  // stack of errors in a 280px popover is a wall.
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** Hand a thread's room to the panel, if what came back is one. */
  const enter = (thread: SupportThread) => {
    const room = parseRoomId(thread.room);
    if (room) onOpen(room);
  };

  const start = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || topic === null) return;
    setBusy(true);
    const line = subject.trim();
    const done = await support.open(topic, line === '' ? undefined : line);
    setBusy(false);
    if (!done.ok) {
      setFailed(refusal(done.reason, s));
      return;
    }
    setFailed(null);
    setSubject('');
    setTopic(null);
    // Straight into the room, because the form collected the filing and not
    // the question. What the learner actually came to say is the first
    // message, and leaving them in a popover with a "started" confirmation
    // would be asking them to find the way in by themselves.
    enter(done.thread);
  };

  const close = async () => {
    if (busy) return;
    setBusy(true);
    const done = await support.resolve();
    setBusy(false);
    setFailed(done ? null : s.support.failed);
  };

  /*
   * The three blocks below are functions returning elements, and are called
   * rather than mounted. A nested component would be a new type on every
   * render, which React unmounts and remounts — and the subject field would
   * lose focus on every keystroke.
   */

  /** The conversation they have going. At most one ever exists. */
  const going = (thread: SupportThread): ReactNode => {
    const room = parseRoomId(thread.room);
    return (
      <section className="chat-support-block">
        <p className="label">{s.support.yours}</p>
        <div className={room !== null && room === active ? 'chat-support-thread is-here' : 'chat-support-thread'}>
          <p className="chat-support-line">
            <span className="chat-support-topic">{s.support.topics[thread.topic]}</span>
            <Tag tone={thread.state === 'open' ? 'on' : 'off'}>
              {thread.state === 'open' ? s.support.stateOpen : s.support.stateResolved}
            </Tag>
          </p>
          {/* The subject is a line this learner wrote and takes its own
              direction, whichever script the panel is in. */}
          {thread.subject !== null && (
            <p className="chat-support-subject" dir="auto">
              {thread.subject}
            </p>
          )}
          <p className="note">{thread.lastFrom === 'staff' ? s.support.replied : s.support.waiting}</p>
          <p className="note">
            {thread.agent !== null ? (
              <Parts
                of={s.support.withAgent(
                  <span key="agent" dir="auto">
                    {thread.agent}
                  </span>,
                )}
              />
            ) : (
              s.support.unclaimed
            )}
          </p>
          <p className="note">{s.support.opened(shortDate(new Date(thread.opened), locale))}</p>
          <div className="chat-support-actions">
            {/* Not drawn at all when the room id is one this bundle cannot
                parse. A button that opens nothing is worse than an absent
                one, and everything else here still says where they stand. */}
            {room !== null && (
              <Button type="button" variant="solid" title={s.support.openRoomWhy} onClick={() => onOpen(room)}>
                {s.support.openRoom}
              </Button>
            )}
            {/* Shown and refused rather than hidden once it is already
                closed — the message menu's rule — because the route would
                answer 404 to a second close and the panel would report "that
                did not work" for something that had, in fact, worked. */}
            <Button
              type="button"
              variant="quiet"
              disabled={busy || thread.state === 'resolved'}
              title={s.support.resolveWhy}
              onClick={() => void close()}
            >
              {s.support.resolveIt}
            </Button>
          </div>
        </div>
      </section>
    );
  };

  /** Starting one: the filing, and nothing else. The question goes in the room. */
  const form = (): ReactNode => (
    <form className="chat-support-new" onSubmit={(e) => void start(e)}>
      <p className="label" id="chat-support-about">
        {s.support.about}
      </p>
      {/* Toggle buttons rather than radios. The five are a closed set and one
          is chosen, which is a radio group's job — but a radio group owes the
          reader arrow-key roving between its members, and a broken one is
          worse than a set of pressed buttons that every keyboard and every
          screen reader already handles. `.chat-room-tag` is the panel's
          existing "a tag you can press".

          `tasks` is not offered: paid tasks retired on 2026-10-31. It stays in
          the protocol's closed list (packages/chat) and keeps its label, so a
          conversation filed under it before then still says what it was. */}
      <div className="chat-support-choices" role="group" aria-labelledby="chat-support-about">
        {SUPPORT_TOPICS.filter((t) => t !== 'tasks').map((t) => (
          <button
            key={t}
            type="button"
            className="tag chat-room-tag chat-support-choice"
            aria-pressed={topic === t}
            onClick={() => setTopic(topic === t ? null : t)}
          >
            {s.support.topics[t]}
          </button>
        ))}
      </div>
      <label className="label" htmlFor="chat-support-subject">
        {s.support.subjectLabel}
      </label>
      <input
        id="chat-support-subject"
        className="chat-input"
        type="text"
        value={subject}
        maxLength={SUPPORT_LIMITS.subject}
        autoComplete="off"
        // Whatever the panel's language, this line is the learner's own.
        dir="auto"
        onChange={(e) => setSubject(e.target.value)}
      />
      {/* Refused rather than hidden until a topic is picked: a button that
          appears once you get something right teaches nobody what was
          missing. The message menu's rule, applied to a form. */}
      <Button type="submit" variant="solid" disabled={busy || topic === null}>
        {s.support.start}
      </Button>
    </form>
  );

  /** One closed conversation. Still a room, so the transcript is one press away. */
  const past = (thread: SupportThread): ReactNode => {
    const room = parseRoomId(thread.room);
    if (room === null) return null;
    return (
      <button
        key={thread.id}
        type="button"
        className={room === active ? 'chat-support-past is-here' : 'chat-support-past'}
        title={s.support.openRoomWhy}
        onClick={() => onOpen(room)}
      >
        <span className="chat-support-topic">{s.support.topics[thread.topic]}</span>
        {thread.subject !== null && (
          <span className="chat-support-subject" dir="auto">
            {thread.subject}
          </span>
        )}
        <span className="msg-meta">{s.support.opened(shortDate(new Date(thread.opened), locale))}</span>
      </button>
    );
  };

  const body = (): ReactNode => {
    if (!signedIn) return <p className="note">{s.support.signedOut}</p>;

    // No door, for any of the four reasons the snapshot cannot tell apart.
    // The first load is not one of them, so it says so rather than reporting
    // a closed desk it has not asked about yet.
    if (!support.enabled && support.thread === null) {
      return support.loading ? <p className="note">{s.support.loading}</p> : <p className="chat-empty">{s.support.closed}</p>;
    }

    return (
      <>
        {/* How many people could pick this up, above everything, because it
            is the fact that decides whether starting one is worth it. */}
        <p className="note">{s.support.here(support.staffOnline)}</p>

        {support.thread !== null ? going(support.thread) : form()}

        {support.recent.length > 0 && (
          <section className="chat-support-block">
            <p className="label">{s.support.past}</p>
            {support.recent.map(past)}
          </section>
        )}

        {failed !== null && (
          <p className="note" role="status">
            {failed}
          </p>
        )}
      </>
    );
  };

  return (
    <div className="chat-support">
      {body()}

      {/* The sentence this feature is allowed to exist on. Last, under
          everything it is about, and in every state — including the two
          where there is nothing else to say. */}
      <p className="note chat-support-privacy">{s.support.privacy}</p>
    </div>
  );
}

/**
 * Why a conversation could not be started, in this reader's language.
 *
 * The three outcomes `lib/support.ts` tells apart, and each one gets its own
 * sentence because the reader's next move differs: come back tomorrow, there
 * is nobody here, or try that again. A single "something went wrong" would
 * be the same words for a day's allowance spent and a dropped connection,
 * which is the wording that makes somebody stop trying.
 */
function refusal(reason: 'busy' | 'closed' | 'other', s: ReturnType<typeof useStrings>['chat']): string {
  switch (reason) {
    case 'busy':
      return s.support.busy;
    case 'closed':
      return s.support.closed;
    default:
      return s.support.failed;
  }
}

export default Support;
